import { FC, KeyboardEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Field, FormikProvider, useFormik } from 'formik';
import cn from 'classnames';

import { useGetProjectQuery, useUpdateProjectMutation, useGetSharedProjectsListQuery } from 'services';
import { Icon, Text } from 'shared/components/ui';
import { InlineInput } from 'shared/components';
import {
	createProjectFormValidationSchema,
	EditShareProjectModal,
	EmptyProjectRequest,
	UnshareProjectModal
} from 'features/projects';

import styles from './index.module.scss';

interface Props {
	className?: string;
}

export const ProjectName: FC<Props> = ({ className, ...otherProps }) => {
	const { data: shared } = useGetSharedProjectsListQuery();
	const [isEditing, setIsEditing] = useState(false);
	const { id } = useParams();
	const projectId = Number(id);
	const { data: ownProjectData } = useGetProjectQuery(Number(id));
	const [updateProject] = useUpdateProjectMutation();
	const [openModal, setOpenModal] = useState(false);
	const [userToUnshare, setUserToUnshare] = useState<string | null>(null);
	const [confirmOpenModal, setConfirmOpenModal] = useState(false);

	const isSharedProject = shared?.projects?.some(project => project.id === projectId);
	const matchedSharedProject = shared?.projects?.find(project => project.id === projectId);
	const data = isSharedProject ? matchedSharedProject : ownProjectData;

	const showSharedIcon = data?.shared_with_emails && data?.shared_with_emails.length > 0;

	const handleSubmit = async (values: EmptyProjectRequest) => {
		if (!data) return;
		try {
			await updateProject({ ...data, name: values.name }).unwrap();
			setIsEditing(false);
		} catch (e: any) {
			if (e.data && e.data.name) {
				formik.setErrors({ name: 'Project with this name already exists' });
				return;
			}
			console.error(e);
		}
	};

	const formik = useFormik<EmptyProjectRequest>({
		validationSchema: createProjectFormValidationSchema,
		enableReinitialize: true,
		initialValues: { name: data?.name || '' },
		onSubmit: values => {
			if (!formik.dirty) {
				setIsEditing(false);
				return;
			}

			if (formik.isValid && formik.dirty) {
				handleSubmit(values);
			}
		}
	});

	const startEditing = () => {
		setIsEditing(true);
	};

	const handleEnterPress = (e: KeyboardEvent<HTMLElement>) => {
		if (e.key === 'Enter') formik.handleSubmit();
	};

	const handleOpenModal = () => {
		setOpenModal(true);
	};

	const handleCloseModal = () => {
		setOpenModal(false);
	};

	const openUnshareModal = (user: string) => () => {
		setUserToUnshare(user);
		setOpenModal(false);
		setConfirmOpenModal(true);
	};

	const closeUnshareModal = () => {
		setConfirmOpenModal(false);
	};

	if (!data) return null;

	return (
		<>
			<div {...otherProps} className={cn(styles.name, className)}>
				{isEditing ? (
					<FormikProvider value={formik}>
						<Field
							name="name"
							onKeyDown={handleEnterPress}
							className={styles.editInput}
							component={InlineInput}
							onBlur={formik.handleSubmit}
						/>
					</FormikProvider>
				) : (
					<button onClick={startEditing} className={styles.edit}>
						<Text variant="inter/14/bold" className={styles.editText}>
							{data.name}
						</Text>
						<Icon icon="pencil" size={16} className={styles.editIcon} />
					</button>
				)}
				{showSharedIcon && <Icon icon="users" size={16} className={styles.icon} onClick={handleOpenModal} />}
			</div>
			{openModal && (
				<EditShareProjectModal
					onClose={handleCloseModal}
					projectId={projectId}
					user_email={data?.user_email!}
					shared_user_emails={data?.shared_with_emails!}
					handleClick={openUnshareModal}
					ownProject={!isSharedProject}
				/>
			)}
			{userToUnshare && confirmOpenModal && (
				<UnshareProjectModal onClose={closeUnshareModal} projectId={projectId} user_email={userToUnshare} />
			)}
		</>
	);
};
